"use client"

import { useMemo } from "react"
import { usePathname } from "next/navigation"
import { useFabCorner, type FabCorner } from "@/hooks/use-fab-corner"
import { usePageHasBottomActions } from "@/hooks/use-fab-bottom-actions"
import { usePageDirection } from "@/hooks/use-page-direction"
import type { TextDirection } from "@/lib/i18n/direction"
import { useUIStore } from "@/stores/ui-store"

// The marker plumbing lives in use-fab-bottom-actions.ts (a spec that stubs this module must not stub it away); the old import path still works.
export { FAB_ACTIONS_ATTR, FAB_ACTIONS_SELECTOR, usePageHasBottomActions, subscribeToBottomActions } from "@/hooks/use-fab-bottom-actions"

/**
 * Heuristic: pages where the operator is mid-task creating or editing a
 * primary entity. The Submit Feedback FAB shouldn't sit at the bottom corner
 * at the inline end there (the bottom-right in English, the bottom-left in
 * Arabic) because every wizard's primary action (Next / Submit / Send) lives
 * in that exact corner — the FAB was eating real clicks (a 2026-05-12
 * Playwright run caught it intercepting the campaign wizard's Next button).
 *
 * Match policy:
 *   - any path segment equal to or ending with "new" → creation page
 *   - any segment containing "create" or "edit" → mutation flow
 * We deliberately use word-boundary segments so we don't false-trigger on
 * "/users/news" or "/newsletters" — the test below catches that.
 */
export function isMutationRoute(pathname: string | null): boolean {
  if (!pathname) return false
  const segments = pathname.split("/").filter(Boolean)
  return segments.some(
    (s) =>
      s === "new" ||
      s === "create" ||
      // #177: /recipients/add and /…/configure have a bottom-right primary CTA
      // (Add Recipient / Continue) that the fixed bottom-right FAB overlapped —
      // relocate the FAB on these creation/wizard routes too.
      s === "add" ||
      s === "edit" ||
      s === "configure" ||
      s.endsWith("-new") ||
      s.endsWith("-create") ||
      s.endsWith("-edit"),
  )
}

/**
 * The bottom corner at the inline START: the bottom-left in English, the
 * bottom-right in Arabic. Primary actions (a wizard's Next, a dialog's
 * confirm) sit at the inline end, so this is the corner that keeps clear of
 * them. The FAB's corners themselves stay physical; only the choice of which
 * one to step aside to follows the text direction.
 */
export function inlineStartBottomCorner(direction: TextDirection): FabCorner {
  return direction === "rtl" ? "bottom-right" : "bottom-left"
}

/**
 * Effective FAB corner — combines the operator's saved preference with two
 * auto-relocate triggers:
 *
 *   (1) route contains a creation / edit segment ("new", "create", "edit"),
 *       unless the page declares a bottom action bar (see liftAboveActions)
 *   (2) at least one modal / dialog / sheet is currently open
 *
 * When either trigger fires we move the FAB to the bottom corner at the
 * **inline start** (inlineStartBottomCorner) so the primary action area at
 * the inline end of the page or modal stays free. It used to be the
 * bottom-left in every language, which in Arabic is where the wizard's Next
 * button sits. When neither trigger fires we fall back to the operator's saved
 * corner, which is physical in every language.
 *
 * The auto-override is purely runtime — the saved preference in
 * localStorage is never mutated, so as soon as the wizard closes or the
 * modal dismisses, the FAB animates back to wherever the operator put it.
 *
 * `direction` is the page's text direction (usePageDirection: <html dir>,
 * which leads the React locale on a hard load in Arabic; following the
 * locale padded the wrong side of the page until the catalogue arrived),
 * returned so the host can mirror the table reserve onto <body>
 * (syncFabReserveAttr).
 *
 * `liftAboveActions` is true while the page on screen says its action bar is
 * along the bottom edge (FAB_ACTIONS_SELECTOR): at a bottom corner the launcher
 * then rests one action band higher (fabCornerTransformFor), and the creation-
 * route rule does not apply to that page: the launcher stays in the operator's
 * own corner and lifts above the bar instead of stepping to the inline start
 * (which, on the survey wizard, is where the cards' left edge is). A dialog
 * that is open still moves it to the inline start.
 */
export function useFabAutoCorner(): {
  effectiveCorner: FabCorner
  savedCorner: FabCorner
  isAutoRelocated: boolean
  liftAboveActions: boolean
  direction: TextDirection
} {
  const fab = useFabCorner()
  const pathname = usePathname()
  const modalOpenCount = useUIStore((s) => s.modalOpenCount)
  const direction = usePageDirection()
  const liftAboveActions = usePageHasBottomActions()

  // A creation route steps aside only when the page does not say where its action bar is. The route rule exists
  // because a wizard's Next sits at the inline end, under the launcher's resting corner; a page that declares its
  // bottom bar (liftAboveActions) is cleared by the lift instead, and moving it to the inline start as well put the
  // launcher over the left edge of the cards on the survey wizard (QA, 17 Oct). An open dialog still steps aside.
  const isAutoRelocated = useMemo(
    () => (isMutationRoute(pathname) && !liftAboveActions) || modalOpenCount > 0,
    [pathname, liftAboveActions, modalOpenCount],
  )

  const effectiveCorner: FabCorner = isAutoRelocated ? inlineStartBottomCorner(direction) : fab.corner

  return {
    effectiveCorner,
    savedCorner: fab.corner,
    isAutoRelocated,
    liftAboveActions,
    direction,
  }
}

/**
 * Translation values for each corner — anchor the FAB at (top:0,left:0) and
 * use `transform: translate(...)` so corner swaps interpolate smoothly via
 * CSS transition. `bottom`/`right` ↔ `auto` toggles cannot transition.
 *
 * Margin = 1.5rem (matches the previous `bottom-6 right-6` Tailwind v4 default).
 * `100% / 100vw` resolve relative to the element's box / viewport at paint
 * time so the translate stays accurate across responsive viewports.
 */
export const FAB_CORNER_TRANSFORM: Record<FabCorner, string> = {
  "bottom-right": "translate(calc(100vw - 100% - 1.5rem), calc(100vh - 100% - 1.5rem))",
  "bottom-left": "translate(1.5rem, calc(100vh - 100% - 1.5rem))",
  "top-right": "translate(calc(100vw - 100% - 1.5rem), 1.5rem)",
  "top-left": "translate(1.5rem, 1.5rem)",
}

/**
 * #204 / #230 — the fixed 48×48 Feedback FAB rests 1.5rem off whichever corner
 * the operator parked it in. Only at the bottom corner at the **inline end**
 * (the bottom-right in English, the bottom-left in Arabic) does it sit over a
 * data table's trailing controls — the sticky pagination footer (#204: the
 * Next / Last buttons) and the last Actions column (#230). At the bottom
 * corner at the inline start the app sidebar occupies that corner (the table
 * starts beside it), and the top corners clear the table body entirely, so no
 * table is ever covered there. Data tables therefore only need to reserve
 * clearance while the FAB is at the inline-end bottom corner.
 *
 * `direction` defaults to left-to-right, the way the page renders before a
 * saved language loads.
 */
export function fabOverlapsTables(corner: FabCorner, direction: TextDirection = "ltr"): boolean {
  return corner === (direction === "rtl" ? "bottom-left" : "bottom-right")
}

/** The physical side of a table's pagination bar the FAB covers. */
export type FabReserveSide = "left" | "right"

/**
 * Which side of the pagination bar to give up to the FAB, or null when it
 * covers no table. Physical, because the FAB's corner is: a right-hand FAB
 * takes room from the bar's right end in every language.
 */
export function fabReserveSide(corner: FabCorner, direction: TextDirection = "ltr"): FabReserveSide | null {
  if (!fabOverlapsTables(corner, direction)) return null
  return corner.endsWith("right") ? "right" : "left"
}

/**
 * The attribute the pure-CSS safe-area rules key on (`body[data-fab-reserve=
 * "right"|"left"] [data-fab-safe-area] [data-fab-footer]` in globals.css).
 * Its value names the physical side to pad. Living on <body> lets every data
 * table reserve room without knowing where the FAB is — the table only tags
 * itself `data-fab-safe-area`; this attribute decides when and where that
 * reservation is live. The server renders it empty (app/layout.tsx), which the
 * stylesheet reads as the default bottom-right corner until the host mounts.
 */
export const FAB_RESERVE_ATTR = "data-fab-reserve"

/**
 * Mirror the FAB's table-overlap side onto <body>. Imperative (not
 * React-rendered) so the attribute survives the FAB portal unmounting
 * mid-modal, mirroring how the modal counter is kept. Safe to call during SSR
 * (no-op).
 */
export function syncFabReserveAttr(corner: FabCorner, direction: TextDirection = "ltr"): void {
  if (typeof document === "undefined") return
  const side = fabReserveSide(corner, direction)
  if (side) {
    document.body.setAttribute(FAB_RESERVE_ATTR, side)
  } else {
    document.body.removeAttribute(FAB_RESERVE_ATTR)
  }
}
